import axios from 'axios';

import { defineStore } from 'pinia';
import { AddAnIdoFavoriteCommand } from 'src/stores/organization/commands/AddAnIdoFavoriteCommand';
import { RemoveAnIdoFavoriteCommand } from 'src/stores/organization/commands/RemoveAnIdoFavoriteCommand';
import { ShareTheSharedQueryCommand } from 'src/stores/organization/commands/ShareTheSharedQueryCommand';
import { UnShareTheSharedQueryCommand } from 'src/stores/organization/commands/UnShareTheSharedQueryCommand';
import { UpdateIdoFavoriteOrderCommand } from 'src/stores/organization/commands/UpdateIdoFavoriteOrderCommand';
import {
	GetTheFavoriteIdosForTheUserQuery,
	GetTheOrganizationTreeForTheUserQuery,
	GetTheSharedQueriesForTheUserAsListQuery,
	GetTheSharedQueriesForTheUserQuery,
} from 'src/stores/organization/queries';
import { v4 as uuidV4 } from 'uuid';

import {
	FolderExplorerTreeNode,
	IdoFavorite,
	IdoFavoriteRunType,
	NotebookTreeNode,
	OrganizationTreeNode,
	OrganizationTreeNodeRunType,
	SharedQueriesTreeAsList,
	SharedQueriesTreeAsListRunType,
	SharedQueryTreeNode,
} from 'src/models/organization';

import {
	GetTheFavoriteIdosForTheUserQueryCallBack,
	GetTheOrganizationTreeForTheUserQueryCallBack,
	GetTheSharedQueriesForTheUserAsListCallBack,
	GetTheSharedQueriesForTheUserQueryCallBack,
} from './callbacks';
import {
	CreateDesktopSharedQueryPayload,
	CreateIdoSharedQueryPayload,
	FetchOrganizationChildrenPayload,
	FetchOrganizationPayload,
	OrganizationCommandPayload,
	OrganizationNotebookActivitiesMutationPayload,
	OrganizationNotebookMutationPayload,
	OrganizationSharedQueryMutationPayload,
	OrganizationStoreState,
	ShareSharedQueryPayload,
	UnshareSharedQueryPayload,
	UpdateIdoFavoriteOrderPayload,
} from './interfaces';
import { mapNotebookTreeNodes, mapSharedQueryTreeNodes } from './treeAdapters';

const ROOT_NOTEBOOK_ID = 'tree_notebooks';
const ROOT_SHARED_QUERY_ID = 'tree_sharedQueries';

const sortIdoFavorites = (payload: Array<IdoFavorite>): Array<IdoFavorite> =>
	[...payload].sort((a, b) => (a.position < b.position ? -1 : 1));

const sortSharedQueries = (payload: Array<SharedQueriesTreeAsList>): Array<SharedQueriesTreeAsList> =>
	[...payload].sort((a, b) => a.name.localeCompare(b.name));

const resolveActiveOrganizationBaseUrl = async (): Promise<null | string> => {
	const { useAuthenticationStore } = await import('src/stores/authentication');
	return useAuthenticationStore().activeInstanceAuthenticationConfiguration?.url ?? null;
};

const resolveOrganizationMutationUrl = async (path: string): Promise<null | string> => {
	const baseUrl = await resolveActiveOrganizationBaseUrl();

	if (baseUrl === null) {
		return null;
	}

	return `${baseUrl}${path}`;
};

const detachTreeNode = <T extends OrganizationTreeNode>(
	nodes: T[],
	nodeId: string
): { detachedNode: T | null; remainingNodes: T[] } => {
	let detachedNode: T | null = null;

	const remainingNodes = nodes.flatMap((node) => {
		if (node.id === nodeId) {
			detachedNode = node;
			return [];
		}

		if (!Array.isArray(node.children) || node.children.length === 0) {
			return [node];
		}

		const detachedChild = detachTreeNode(node.children as T[], nodeId);
		if (detachedChild.detachedNode === null) {
			return [node];
		}

		detachedNode = detachedChild.detachedNode;
		return [
			{
				...node,
				children: detachedChild.remainingNodes,
			},
		];
	});

	return {
		detachedNode,
		remainingNodes,
	};
};

const insertTreeNodeIntoParent = <T extends OrganizationTreeNode>(
	nodes: T[],
	parentId: string,
	nodeToInsert: T
): { inserted: boolean; nextNodes: T[] } => {
	let inserted = false;

	const nextNodes = nodes.map((node) => {
		if (node.id === parentId) {
			inserted = true;
			return {
				...node,
				children: [...((node.children as T[] | undefined) ?? []), nodeToInsert],
			};
		}

		if (!Array.isArray(node.children) || node.children.length === 0) {
			return node;
		}

		const nestedInsert = insertTreeNodeIntoParent(node.children as T[], parentId, nodeToInsert);
		if (!nestedInsert.inserted) {
			return node;
		}

		inserted = true;
		return {
			...node,
			children: nestedInsert.nextNodes,
		};
	});

	return {
		inserted,
		nextNodes,
	};
};

const moveTreeNodeToParent = <T extends OrganizationTreeNode>(
	nodes: T[] | null,
	nodeId: string,
	parentId: string,
	rootId: string
): T[] | null => {
	if (nodes === null || nodeId === parentId) {
		return nodes;
	}

	const detachedTreeNode = detachTreeNode(nodes, nodeId);
	if (detachedTreeNode.detachedNode === null) {
		return nodes;
	}

	if (parentId === rootId) {
		return [...detachedTreeNode.remainingNodes, detachedTreeNode.detachedNode];
	}

	const insertedTreeNode = insertTreeNodeIntoParent(
		detachedTreeNode.remainingNodes,
		parentId,
		detachedTreeNode.detachedNode
	);
	return insertedTreeNode.inserted ? insertedTreeNode.nextNodes : nodes;
};

const replaceTreeNodeChildren = <T extends OrganizationTreeNode>(nodes: T[], nodeId: string, children: T[]): T[] => {
	return nodes.map((node) => {
		if (node.id === nodeId) {
			return {
				...node,
				children,
			};
		}

		if (!Array.isArray(node.children) || node.children.length === 0) {
			return node;
		}

		return {
			...node,
			children: replaceTreeNodeChildren(node.children as T[], nodeId, children),
		};
	});
};

const updateTreeNodeActivityCount = <T extends OrganizationTreeNode>(
	nodes: T[],
	nodeId: string,
	activityCount: number
): T[] => {
	return nodes.map((node) => {
		if (node.id === nodeId) {
			return {
				...node,
				activityCount,
			};
		}

		if (!Array.isArray(node.children) || node.children.length === 0) {
			return node;
		}

		return {
			...node,
			children: updateTreeNodeActivityCount(node.children as T[], nodeId, activityCount),
		};
	});
};

const createDefaultState = (): OrganizationStoreState => ({
	idoFavorites: [],
	notebookTree: null,
	notebookTreeError: null,
	notebookTreeLoading: false,
	sharedQueryTree: null,
	sharedQueryTreeHierarchy: null,
	sharedQueryTreeHierarchyError: null,
	sharedQueryTreeHierarchyLoading: false,
});

const normalizeOrganizationNodeNameForSort = (name: string): string =>
	name
		.replace(/\s\([0-9]+\)$/, '')
		.trim()
		.toLocaleLowerCase();

const sortTreeNodesRecursively = <T extends OrganizationTreeNode>(nodes: T[] | null): T[] | null => {
	if (nodes === null) {
		return null;
	}

	return [...nodes]
		.map((node) => ({
			...node,
			children: Array.isArray(node.children) ? sortTreeNodesRecursively(node.children as T[]) : [],
		}))
		.sort((leftNode, rightNode) =>
			normalizeOrganizationNodeNameForSort(leftNode.name).localeCompare(
				normalizeOrganizationNodeNameForSort(rightNode.name),
				undefined,
				{ numeric: true, sensitivity: 'base' }
			)
		) as T[];
};

const prependTreeNodeIntoParent = <T extends OrganizationTreeNode>(
	nodes: T[],
	parentId: string,
	nodeToInsert: T
): { inserted: boolean; nextNodes: T[] } => {
	let inserted = false;

	const nextNodes = nodes.map((node) => {
		if (node.id === parentId) {
			inserted = true;
			return {
				...node,
				children: [nodeToInsert, ...((node.children as T[] | undefined) ?? [])],
			};
		}

		if (!Array.isArray(node.children) || node.children.length === 0) {
			return node;
		}

		const nestedInsert = prependTreeNodeIntoParent(node.children as T[], parentId, nodeToInsert);
		if (!nestedInsert.inserted) {
			return node;
		}

		inserted = true;
		return {
			...node,
			children: nestedInsert.nextNodes,
		};
	});

	return {
		inserted,
		nextNodes,
	};
};

const prependTreeNodeToParent = <T extends OrganizationTreeNode>(
	nodes: T[] | null,
	parentId: string,
	rootId: string,
	nodeToInsert: T
): T[] | null => {
	if (nodes === null) {
		return nodes;
	}

	if (parentId === rootId) {
		return [nodeToInsert, ...nodes];
	}

	const insertedTreeNode = prependTreeNodeIntoParent(nodes, parentId, nodeToInsert);
	return insertedTreeNode.inserted ? insertedTreeNode.nextNodes : nodes;
};

const removeTreeNode = <T extends OrganizationTreeNode>(nodes: T[] | null, nodeId: string): T[] | null => {
	if (nodes === null) {
		return null;
	}

	return nodes.reduce<T[]>((remainingNodes, node) => {
		if (node.id === nodeId) {
			return remainingNodes;
		}

		remainingNodes.push({
			...node,
			children: Array.isArray(node.children) ? (removeTreeNode(node.children as T[], nodeId) ?? []) : [],
		} as T);

		return remainingNodes;
	}, []);
};

const renameTreeNode = <T extends OrganizationTreeNode>(
	nodes: T[] | null,
	nodeId: string,
	name: string
): T[] | null => {
	if (nodes === null) {
		return null;
	}

	return nodes.map((node) => {
		if (node.id === nodeId) {
			return {
				...node,
				name,
			} as T;
		}

		return {
			...node,
			children: Array.isArray(node.children) ? (renameTreeNode(node.children as T[], nodeId, name) ?? []) : [],
		} as T;
	});
};

const createLocalNotebookTreeNode = (payload: OrganizationNotebookMutationPayload): NotebookTreeNode => ({
	children: [],
	id: payload.nodeId,
	identifier: payload.nodeId,
	leaf: false,
	name: payload.name ?? '',
	type: 'notebook',
});

const createLocalSharedQueryContainerNode = (payload: OrganizationSharedQueryMutationPayload): SharedQueryTreeNode => ({
	children: [],
	id: payload.nodeId,
	identifier: payload.nodeId,
	leaf: false,
	name: payload.name ?? '',
	type: 'sharedQueriesContainer',
});

export const useOrganizationStore = defineStore('organization', {
	actions: {
		async addActivitiesToNotebook(payload: OrganizationNotebookActivitiesMutationPayload): Promise<null | number> {
			const { useAuthenticationStore } = await import('src/stores/authentication');
			const authStore = useAuthenticationStore();
			const config = authStore.activeInstanceAuthenticationConfiguration;
			if (config === null) {
				return null;
			}

			const response = await axios.post<{ activityCount: number; success: boolean }>(
				`${config.url}/organization/notebookstree/add-activities-to-notebook`,
				new URLSearchParams({
					aggregateIds: JSON.stringify(payload.aggregateIds),
					nodeId: payload.nodeId,
				})
			);

			if (response.data?.success) {
				return response.data.activityCount;
			}

			return null;
		},
		addAnIdoFavorite(payload: OrganizationCommandPayload): void {
			void payload.commandBus?.sendCommand(new AddAnIdoFavoriteCommand(payload.idoId, payload.idoInstanceId));
		},
		async createDesktopSharedQuery(payload: CreateDesktopSharedQueryPayload): Promise<null | string> {
			const mutationUrl = await resolveOrganizationMutationUrl(
				'/organization/sharedqueriestree/create-a-shared-query'
			);
			if (mutationUrl === null) {
				return null;
			}

			const sharedQueryId = uuidV4();
			await axios.post(
				mutationUrl,
				new URLSearchParams({
					desktopApplicationItemId: payload.desktopApplicationItemId,
					serializedDesktopModuleFilterMemento: payload.serializedDesktopModuleFilterMemento,
					sharedQueryId,
					sharedQueryName: payload.name,
				})
			);

			if (payload.queryStore !== undefined) {
				await this.fetchSharedQueries({
					queryStore: payload.queryStore,
				});
				await this.fetchSharedQueriesTree({
					force: true,
					queryStore: payload.queryStore,
				});
			}

			return sharedQueryId;
		},
		async createIdoSharedQuery(payload: CreateIdoSharedQueryPayload): Promise<null | string> {
			const mutationUrl = await resolveOrganizationMutationUrl(
				'/organization/sharedqueriestree/create-an-ido-shared-query'
			);
			if (mutationUrl === null) {
				return null;
			}

			const sharedQueryId = uuidV4();
			await axios.post(
				mutationUrl,
				new URLSearchParams({
					idoIdoListToolbarCalculationIsActive: String(payload.idoIdoListToolbarCalculationIsActive ?? false),
					idoInstanceId: payload.idoInstanceId,
					serializedIdoIdoListToolbarHiddenDetailsSearchString: payload.serializedDetailsSearch,
					sharedQueryId,
					sharedQueryName: payload.name,
				})
			);

			if (payload.queryStore !== undefined) {
				await this.fetchSharedQueries({
					queryStore: payload.queryStore,
				});
				await this.fetchSharedQueriesTree({
					force: true,
					queryStore: payload.queryStore,
				});
			}

			return sharedQueryId;
		},
		async createNotebook(payload: OrganizationNotebookMutationPayload): Promise<void> {
			const mutationUrl = await resolveOrganizationMutationUrl('/organization/notebookstree/update');
			if (mutationUrl === null) {
				return;
			}

			await axios.post(mutationUrl, {
				id: payload.nodeId,
				name: payload.name ?? '',
				parentId: payload.parentId ?? ROOT_NOTEBOOK_ID,
			});

			this.setNotebookTree(
				prependTreeNodeToParent(
					this.notebookTree,
					payload.parentId ?? ROOT_NOTEBOOK_ID,
					ROOT_NOTEBOOK_ID,
					createLocalNotebookTreeNode(payload)
				)
			);
		},
		async createSharedQueryContainer(payload: OrganizationSharedQueryMutationPayload): Promise<void> {
			const mutationUrl = await resolveOrganizationMutationUrl('/organization/sharedqueriestree/update');
			if (mutationUrl === null) {
				return;
			}

			await axios.post(mutationUrl, {
				id: payload.nodeId,
				name: payload.name ?? '',
				parentId: payload.parentId ?? ROOT_SHARED_QUERY_ID,
			});

			this.setSharedQueryTreeHierarchy(
				prependTreeNodeToParent(
					this.sharedQueryTreeHierarchy,
					payload.parentId ?? ROOT_SHARED_QUERY_ID,
					ROOT_SHARED_QUERY_ID,
					createLocalSharedQueryContainerNode(payload)
				)
			);
		},
		fetchIdoFavorites(payload: FetchOrganizationPayload): void {
			if (payload.queryStore !== undefined) {
				void payload.queryStore.query<Array<IdoFavorite>>({
					callback: new GetTheFavoriteIdosForTheUserQueryCallBack((idoFavorites) =>
						this.setIdoFavorites(idoFavorites)
					),
					createTime: new Date(),
					query: new GetTheFavoriteIdosForTheUserQuery(),
					responseType: 'json',
					runType: IdoFavoriteRunType,
				});
				return;
			}

			this.setIdoFavorites(null);
		},
		async fetchNotebookTree(payload: FetchOrganizationPayload): Promise<void> {
			if (this.notebookTree !== null && !payload.force) {
				return;
			}

			if (payload.queryStore === undefined) {
				this.setNotebookTree(null);
				return;
			}

			const shouldToggleLoading = payload.silent !== true;
			if (shouldToggleLoading) {
				this.notebookTreeLoading = true;
			}
			this.notebookTreeError = null;
			try {
				await payload.queryStore.query<Array<NotebookTreeNode>>({
					callback: new GetTheOrganizationTreeForTheUserQueryCallBack((notebookTree) =>
						this.setNotebookTree(notebookTree)
					),
					createTime: new Date(),
					query: new GetTheOrganizationTreeForTheUserQuery(),
					responseType: 'json',
					runType: OrganizationTreeNodeRunType,
				});
			} catch (error) {
				this.notebookTreeError = error instanceof Error ? error.message : 'notebookTreeFetchFailed';
			} finally {
				if (shouldToggleLoading) {
					this.notebookTreeLoading = false;
				}
			}
		},
		async fetchNotebookTreeChildren(payload: FetchOrganizationChildrenPayload): Promise<void> {
			if (payload.queryStore === undefined || this.notebookTree === null) {
				return;
			}

			try {
				await payload.queryStore.query<Array<NotebookTreeNode>>({
					callback: new GetTheOrganizationTreeForTheUserQueryCallBack((notebookChildren) => {
						this.setNotebookTree(
							replaceTreeNodeChildren(this.notebookTree ?? [], payload.nodeId, notebookChildren)
						);
					}, payload.nodeId),
					createTime: new Date(),
					query: new GetTheOrganizationTreeForTheUserQuery(payload.nodeId, payload.parentNodeIdentifier),
					responseType: 'json',
					runType: OrganizationTreeNodeRunType,
				});
			} catch (error) {
				this.notebookTreeError = error instanceof Error ? error.message : 'notebookTreeFetchFailed';
			}
		},
		async fetchSharedQueries(payload: FetchOrganizationPayload): Promise<void> {
			if (payload.queryStore !== undefined) {
				await payload.queryStore.query<Array<SharedQueriesTreeAsList>>({
					callback: new GetTheSharedQueriesForTheUserAsListCallBack((sharedQueries) =>
						this.setSharedQueries(sharedQueries)
					),
					createTime: new Date(),
					query: new GetTheSharedQueriesForTheUserAsListQuery(),
					responseType: 'json',
					runType: SharedQueriesTreeAsListRunType,
				});
				return;
			}

			this.setSharedQueries(null);
		},
		async fetchSharedQueriesTree(payload: FetchOrganizationPayload): Promise<void> {
			if (this.sharedQueryTreeHierarchy !== null && !payload.force) {
				return;
			}

			if (payload.queryStore === undefined) {
				this.setSharedQueryTreeHierarchy(null);
				return;
			}

			const shouldToggleLoading = payload.silent !== true;
			if (shouldToggleLoading) {
				this.sharedQueryTreeHierarchyLoading = true;
			}
			this.sharedQueryTreeHierarchyError = null;
			try {
				await payload.queryStore.query<Array<SharedQueryTreeNode>>({
					callback: new GetTheSharedQueriesForTheUserQueryCallBack((tree) =>
						this.setSharedQueryTreeHierarchy(tree)
					),
					createTime: new Date(),
					query: new GetTheSharedQueriesForTheUserQuery(),
					responseType: 'json',
					runType: OrganizationTreeNodeRunType,
				});
			} catch (error) {
				this.sharedQueryTreeHierarchyError =
					error instanceof Error ? error.message : 'sharedQueryTreeFetchFailed';
			} finally {
				if (shouldToggleLoading) {
					this.sharedQueryTreeHierarchyLoading = false;
				}
			}
		},
		async fetchSharedQueriesTreeChildren(payload: FetchOrganizationChildrenPayload): Promise<void> {
			if (payload.queryStore === undefined || this.sharedQueryTreeHierarchy === null) {
				return;
			}

			try {
				await payload.queryStore.query<Array<SharedQueryTreeNode>>({
					callback: new GetTheSharedQueriesForTheUserQueryCallBack((sharedQueryChildren) => {
						this.setSharedQueryTreeHierarchy(
							replaceTreeNodeChildren(
								this.sharedQueryTreeHierarchy ?? [],
								payload.nodeId,
								sharedQueryChildren
							)
						);
					}, payload.nodeId),
					createTime: new Date(),
					query: new GetTheSharedQueriesForTheUserQuery(payload.nodeId, payload.parentNodeIdentifier),
					responseType: 'json',
					runType: OrganizationTreeNodeRunType,
				});
			} catch (error) {
				this.sharedQueryTreeHierarchyError =
					error instanceof Error ? error.message : 'sharedQueryTreeFetchFailed';
			}
		},
		async moveNotebook(payload: OrganizationNotebookMutationPayload): Promise<void> {
			const baseUrl = await resolveActiveOrganizationBaseUrl();
			if (baseUrl === null) {
				return;
			}

			const previousNotebookTree = this.notebookTree;
			const movedNotebookTree = moveTreeNodeToParent(
				this.notebookTree,
				payload.nodeId,
				payload.parentId ?? ROOT_NOTEBOOK_ID,
				ROOT_NOTEBOOK_ID
			);

			if (movedNotebookTree !== null) {
				this.setNotebookTree(movedNotebookTree);
			}

			try {
				await axios.post(
					`${baseUrl}/organization/notebookstree/update-the-parent-notebook`,
					new URLSearchParams({
						notebookId: payload.nodeId,
						parentNotebookId: payload.parentId ?? ROOT_NOTEBOOK_ID,
					})
				);
			} catch (error) {
				this.setNotebookTree(previousNotebookTree);
				throw error;
			}
		},
		async moveSharedQueryNode(payload: OrganizationSharedQueryMutationPayload): Promise<void> {
			const baseUrl = await resolveActiveOrganizationBaseUrl();
			if (baseUrl === null) {
				return;
			}

			const previousSharedQueryTree = this.sharedQueryTreeHierarchy;
			const movedSharedQueryTree = moveTreeNodeToParent(
				this.sharedQueryTreeHierarchy,
				payload.nodeId,
				payload.parentId ?? ROOT_SHARED_QUERY_ID,
				ROOT_SHARED_QUERY_ID
			);

			if (movedSharedQueryTree !== null) {
				this.setSharedQueryTreeHierarchy(movedSharedQueryTree);
			}

			try {
				await axios.post(
					`${baseUrl}/organization/sharedqueriestree/update-the-parent-shared-queries-container`,
					new URLSearchParams({
						parentSharedQueriesContainerId: payload.parentId ?? ROOT_SHARED_QUERY_ID,
						sharedQueriesContainerId: payload.nodeId,
					})
				);
			} catch (error) {
				this.setSharedQueryTreeHierarchy(previousSharedQueryTree);
				throw error;
			}
		},
		removeAnIdoFavorite(payload: OrganizationCommandPayload): void {
			void payload.commandBus?.sendCommand(new RemoveAnIdoFavoriteCommand(payload.idoId, payload.idoInstanceId));
		},
		async removeNotebook(payload: OrganizationNotebookMutationPayload): Promise<void> {
			const mutationUrl = await resolveOrganizationMutationUrl('/organization/notebookstree/destroy');
			if (mutationUrl === null) {
				return;
			}

			const previousNotebookTree = this.notebookTree;
			this.setNotebookTree(removeTreeNode(this.notebookTree, payload.nodeId));

			try {
				await axios.post(mutationUrl, [{ id: payload.nodeId }]);
			} catch (error) {
				this.setNotebookTree(previousNotebookTree);
				throw error;
			}
		},
		async removeSharedQueryNode(payload: OrganizationSharedQueryMutationPayload): Promise<void> {
			const mutationUrl = await resolveOrganizationMutationUrl('/organization/sharedqueriestree/destroy');
			if (mutationUrl === null) {
				return;
			}

			const previousSharedQueryTree = this.sharedQueryTreeHierarchy;
			this.setSharedQueryTreeHierarchy(removeTreeNode(this.sharedQueryTreeHierarchy, payload.nodeId));

			try {
				await axios.post(mutationUrl, [{ id: payload.nodeId }]);
			} catch (error) {
				this.setSharedQueryTreeHierarchy(previousSharedQueryTree);
				throw error;
			}
		},
		async renameNotebook(payload: OrganizationNotebookMutationPayload): Promise<void> {
			const mutationUrl = await resolveOrganizationMutationUrl('/organization/notebookstree/update');
			if (mutationUrl === null) {
				return;
			}

			const previousNotebookTree = this.notebookTree;
			this.setNotebookTree(renameTreeNode(this.notebookTree, payload.nodeId, payload.name ?? ''));

			try {
				await axios.post(mutationUrl, {
					id: payload.nodeId,
					name: payload.name ?? '',
					parentId: payload.parentId ?? ROOT_NOTEBOOK_ID,
				});
			} catch (error) {
				this.setNotebookTree(previousNotebookTree);
				throw error;
			}
		},
		async renameSharedQueryContainer(payload: OrganizationSharedQueryMutationPayload): Promise<void> {
			await this.renameSharedQueryNode(payload);
		},
		async renameSharedQueryNode(payload: OrganizationSharedQueryMutationPayload): Promise<void> {
			const mutationUrl = await resolveOrganizationMutationUrl('/organization/sharedqueriestree/update');
			if (mutationUrl === null) {
				return;
			}

			const previousSharedQueryTree = this.sharedQueryTreeHierarchy;
			this.setSharedQueryTreeHierarchy(
				renameTreeNode(this.sharedQueryTreeHierarchy, payload.nodeId, payload.name ?? '')
			);

			try {
				await axios.post(mutationUrl, {
					id: payload.nodeId,
					name: payload.name ?? '',
					parentId: payload.parentId ?? ROOT_SHARED_QUERY_ID,
				});
			} catch (error) {
				this.setSharedQueryTreeHierarchy(previousSharedQueryTree);
				throw error;
			}
		},
		setIdoFavorites(payload: Array<IdoFavorite> | null): void {
			this.idoFavorites = payload !== null ? sortIdoFavorites(payload) : [];
		},
		setNotebookTree(payload: Array<NotebookTreeNode> | null): void {
			this.notebookTree = sortTreeNodesRecursively(payload);
		},
		setSharedQueries(payload: Array<SharedQueriesTreeAsList> | null): void {
			this.sharedQueryTree = payload !== null ? sortSharedQueries(payload) : null;
		},
		setSharedQueryTreeHierarchy(payload: Array<SharedQueryTreeNode> | null): void {
			this.sharedQueryTreeHierarchy = sortTreeNodesRecursively(payload);
		},
		shareSharedQuery(payload: ShareSharedQueryPayload): void {
			void payload.commandBus?.sendCommand(
				new ShareTheSharedQueryCommand(payload.sharedQueryId, payload.userId ?? null, payload.groupId ?? null)
			);
		},
		unshareSharedQuery(payload: UnshareSharedQueryPayload): void {
			void payload.commandBus?.sendCommand(
				new UnShareTheSharedQueryCommand(payload.sharedQueryId, payload.userId)
			);
		},
		updateIdoFavoriteOrder(payload: UpdateIdoFavoriteOrderPayload): void {
			const reorderedWithPositions = payload.idoFavorites.map((favorite, index) => ({
				...favorite,
				position: index + 1,
			}));

			this.setIdoFavorites(reorderedWithPositions);

			void payload.commandBus?.sendCommand(new UpdateIdoFavoriteOrderCommand(reorderedWithPositions));
		},
		updateNotebookActivityCount(payload: { activityCount: number; nodeId: string }): void {
			if (this.notebookTree === null) {
				return;
			}

			this.notebookTree = updateTreeNodeActivityCount(this.notebookTree, payload.nodeId, payload.activityCount);
		},
	},
	getters: {
		notebookPrimeVueNodes(state): Array<FolderExplorerTreeNode> {
			return state.notebookTree !== null ? mapNotebookTreeNodes(state.notebookTree) : [];
		},
		sharedQueries(state): Array<SharedQueriesTreeAsList> | null {
			return state.sharedQueryTree;
		},
		sharedQueryPrimeVueNodes(state): Array<FolderExplorerTreeNode> {
			return state.sharedQueryTreeHierarchy !== null
				? mapSharedQueryTreeNodes(state.sharedQueryTreeHierarchy)
				: [];
		},
	},
	state: createDefaultState,
});
export * from './interfaces';
export * from './types';
